import { User } from "firebase/auth";
import { createClarityClient } from "../plugins/clarity";

/**
 * Clarity analytics service
 * Handles user identification and custom tracking
 */
class ClarityService {
    private clarityClient = createClarityClient({} as any);
    private isInitialized = false;

    /**
     * Initialize Clarity with user information
     * @param user - Firebase user object
     * @param organizationId - Optional organization ID
     */
    async initializeWithUser(user: User, organizationId?: string): Promise<void> {
        if (this.isInitialized) {
            return;
        }

        try {
            // Initialize Clarity
            this.clarityClient.initialize();

            // Set user identification
            this.clarityClient.setUserId(user.uid);
            this.clarityClient.setCustomTag("userEmail", user.email || "");
            this.clarityClient.setCustomTag("userDisplayName", user.displayName || "");

            if (organizationId) {
                this.clarityClient.setCustomTag("organizationId", organizationId);
            }

            // Set additional user properties
            this.clarityClient.setCustomTag(
                "authProvider",
                user.providerData[0]?.providerId || "unknown",
            );
            this.clarityClient.setCustomTag("isEmailVerified", user.emailVerified.toString());

            this.isInitialized = true;
            console.log("Clarity analytics initialized with user data");
        } catch (error) {
            console.error("Error initializing Clarity with user data:", error);
        }
    }

    /**
     * Update user information in Clarity
     * @param user - Firebase user object
     * @param organizationId - Optional organization ID
     */
    async updateUser(user: User, organizationId?: string): Promise<void> {
        try {
            this.clarityClient.setUserId(user.uid);
            this.clarityClient.setCustomTag("userEmail", user.email || "");
            this.clarityClient.setCustomTag("userDisplayName", user.displayName || "");

            if (organizationId) {
                this.clarityClient.setCustomTag("organizationId", organizationId);
            }

            this.clarityClient.setCustomTag(
                "authProvider",
                user.providerData[0]?.providerId || "unknown",
            );
            this.clarityClient.setCustomTag("isEmailVerified", user.emailVerified.toString());
        } catch (error) {
            console.error("Error updating Clarity user data:", error);
        }
    }

    /**
     * Track custom events
     * @param eventName - The event name
     * @param properties - Optional event properties
     */
    trackEvent(eventName: string, properties?: Record<string, any>): void {
        try {
            if (typeof window !== "undefined" && (window as any).clarity) {
                (window as any).clarity("event", eventName, properties);
            }
        } catch (error) {
            console.error("Error tracking Clarity event:", error);
        }
    }

    /**
     * Set custom tags
     * @param key - The tag key
     * @param value - The tag value
     */
    setCustomTag(key: string, value: string): void {
        this.clarityClient.setCustomTag(key, value);
    }

    /**
     * Clear user data (call on logout)
     */
    clearUser(): void {
        try {
            if (typeof window !== "undefined" && (window as any).clarity) {
                (window as any).clarity("set", "userId", "");
                (window as any).clarity("set", "userEmail", "");
                (window as any).clarity("set", "userDisplayName", "");
                (window as any).clarity("set", "organizationId", "");
            }
        } catch (error) {
            console.error("Error clearing Clarity user data:", error);
        }
    }
}

// Export singleton instance
export const clarityService = new ClarityService();
